/**
 * ActionBar/index.tsx
 *
 * Main ActionBar component. Composes:
 *  - ActionBarStyles (visual primitives, H1 Tome structure with .hudt-ground & .hudt-socket)
 *  - useActionBarDragDrop (drag/drop state & handlers across bars and pages)
 *  - ActionBarSlot (per-slot rendering)
 *  - ActionBarControls (page arrows, page indicator, ⚙ & right-click bar menu)
 */

import React, { useState, useEffect, useCallback, useMemo, useSyncExternalStore } from 'react';
import { ActionBar as ActionBarData, Ability, Helix2000Character } from '../../../../shared/CharacterTypes';
import type { ActionBarAssignment } from '../../../../shared/types/Input';
import {
  barSlotCount,
  mainBar,
  type BarState,
  type SlotRef,
} from '../../../../shared/hotbar/barLayout';
import { ITEM_DATABASE } from '../../../../shared/ItemData';
import type { ItemData } from '../../components/itemkit/shared';
import { gameLogger } from '../../../../utils/GameLogger';
import { abilityRegistry } from '../../../systems/player/abilities/AbilityRegistry';
import { isAbilityTrained } from '../../../../shared/progression/abilityCurve';
import { isPeacefulHotbarAbility } from '../../../../shared/logic/peacefulHotbar';
import { RaceGateStore } from '../../../systems/race/raceGateStore';
import { useActionBarDragDrop } from './useActionBarDragDrop';
import { ActionBarSlot } from './ActionBarSlot';
import { ActionBarControls, type ActionBarLayoutEdit } from './ActionBarMenu';
import { ActionBarContainer, SlotsRow, BarStatusLine, type BarStatusTone } from './ActionBarStyles';
import { BAR_NOTICE_MS, lockLabel, noticeText } from './barStatus';
import { EventBus } from '../../../../shared/EventBus';
import { useKeyBindings } from '../../hooks/useKeyBindings';
import { slotKeyLabel } from '../../../systems/input/keyLabels';
import { toggleHotbarDragLocked, useHotbarDragLocked } from './hotbarDragLockPref';

export type { ActionBarLayoutEdit } from './ActionBarMenu';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ActionBarProps {
  player: Helix2000Character;
  actionBar: ActionBarData;
  abilities: Ability[];
  /** Which bar this component renders (1..6, defaults to 1). */
  barId?: number;
  /** Explicit bar state override; defaults to looking up `barId` in `actionBar.layout.bars`. */
  barState?: BarState;
  /** Layout orientation: horizontal for main/above, vertical for left/right edge bars. */
  orientation?: 'horizontal' | 'vertical';
  onAbilityUse?: (abilityId: string) => void;
  onItemUse?: (itemId: string) => void;
  onSlotUpdate?: (slotIndex: number, assignment: ActionBarAssignment | null) => void;
  /** Bar-and-page-aware slot update for multi-bar/multi-page HUD wiring. */
  onSlotRefUpdate?: (ref: SlotRef, assignment: ActionBarAssignment | null) => void;
  /** Drag one slot onto another (the two swap, within a bar or across bars/pages). */
  onSlotMove?: (from: number | SlotRef, to: number | SlotRef) => void;
  /** Bar and page layout edits (add/remove bar or page, flip page, hide/show, change spot). */
  onLayoutEdit?: (edit: ActionBarLayoutEdit) => void;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * The server hotbar keeps the full class layout so a newly trained ability can
 * appear in its canonical slot immediately. The player-facing bar must not
 * reveal that future kit before it is trained, though. Race-kit grants are the
 * one intentional bypass because they temporarily make cross-class abilities
 * available regardless of the normal progression curve.
 */
function isAbilityVisible(player: Helix2000Character, abilityId: string): boolean {
  return RaceGateStore.isExplicitlyAllowed(abilityId)
    || isPeacefulHotbarAbility((player as Helix2000Character & { peaceful?: boolean }).peaceful, abilityId)
    || isAbilityTrained(player.learnedAbilities, abilityId);
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ActionBar: React.FC<ActionBarProps> = ({
  player,
  actionBar,
  abilities,
  barId = 1,
  barState: barStateProp,
  orientation = 'horizontal',
  onAbilityUse,
  onItemUse,
  onSlotUpdate,
  onSlotRefUpdate,
  onSlotMove,
  onLayoutEdit,
  className = '',
  style = {},
}) => {
  // Cooldown sweeps tick inside each ability slot (`useCooldownTick` in
  // ActionBarSlot), not here: a bar-wide 60 Hz setState re-rendered all ten
  // slots — empty ones included — on every frame of every fight (plan 13 §8).

  const bindings = useKeyBindings();
  const [activatedSlot, setActivatedSlot] = useState<number | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const allBars: ReadonlyArray<BarState> = useMemo(
    () => actionBar.layout?.bars ?? [barStateProp ?? mainBar()],
    [actionBar.layout?.bars, barStateProp],
  );
  const resolvedBar: BarState = useMemo(
    () => barStateProp
      ?? allBars.find((b) => b.id === barId)
      ?? { id: barId, pages: 1, currentPage: 1, visible: true, spot: barId === 1 ? 'main' : 'above' },
    [allBars, barId, barStateProp],
  );
  const currentPage = resolvedBar.currentPage;
  const slotCount = barSlotCount(resolvedBar);
  const visibleSlots = useMemo(
    () => Array.from({ length: slotCount }, (_, i) => i + 1),
    [slotCount],
  );

  // Resolve the slots shown on this bar's current page.
  const pageSlots = useMemo(() => {
    const layoutSlots = actionBar.layout?.slots;
    if (barId === 1) {
      const bar1CurrentPage = actionBar.layout?.bars.find((b) => b.id === 1)?.currentPage ?? 1;
      if (!layoutSlots || actionBar.mode !== 'base' || currentPage === bar1CurrentPage) {
        if (actionBar.slots && actionBar.slots.length > 0) return actionBar.slots;
      }
    }
    if (!layoutSlots) return barId === 1 ? (actionBar.slots ?? []) : [];
    return layoutSlots.filter((s) => s.bar === barId && s.page === currentPage);
  }, [actionBar.layout?.bars, actionBar.layout?.slots, actionBar.mode, actionBar.slots, barId, currentPage]);

  const commitSlotUpdate = useCallback((slotIndex: number, assignment: ActionBarAssignment | null) => {
    if (assignment?.type === 'item' && ITEM_DATABASE[assignment.itemId]?.type !== 'consumable') {
      try { (globalThis as any).__errorToast?.('info', 'Only usable items can go on the action bar'); } catch { /* optional */ }
      return;
    }
    if (onSlotRefUpdate) {
      onSlotRefUpdate({ bar: barId, page: currentPage, slot: slotIndex }, assignment);
    } else {
      onSlotUpdate?.(slotIndex, assignment);
    }
  }, [barId, currentPage, onSlotRefUpdate, onSlotUpdate]);

  // ─── Lock and notices (action-bar plan phase 4) ───────────────────────────
  const locked = actionBar.editable === false;
  const lockText = locked ? lockLabel(actionBar.mode) ?? 'This bar can’t be changed right now' : null;
  const [notice, setNotice] = useState<string | null>(null);
  const noticeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const showNotice = useCallback((text: string) => {
    setNotice(text);
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), BAR_NOTICE_MS);
  }, []);
  useEffect(() => {
    if (barId !== 1) return;
    const off = EventBus.getInstance().on('net:message:hotbarNotice', (n) => showNotice(noticeText(n)));
    return () => {
      off();
      if (noticeTimer.current) clearTimeout(noticeTimer.current);
    };
  }, [barId, showNotice]);

  // ─── Drag & drop ──────────────────────────────────────────────────────────
  const dragLocked = useHotbarDragLocked();
  const handleLockedDragAttempt = useCallback(() => {
    showNotice('🔒 Bar locked — hold Shift to drag or click 🔒 next to ⚙ to unlock');
  }, [showNotice]);
  const [dragState, dragHandlers] = useActionBarDragDrop(
    commitSlotUpdate,
    onSlotMove,
    { bar: barId, page: currentPage },
    { dragLocked, onLockedDragAttempt: handleLockedDragAttempt },
  );
  const onDragOverSlot = (slotIndex: number) => (locked
    ? (e: React.DragEvent) => { e.dataTransfer.dropEffect = 'none'; }
    : dragHandlers.onDragOver(slotIndex));
  const onDropSlot = (slotIndex: number) => (locked
    ? (e: React.DragEvent) => { e.preventDefault(); dragHandlers.onDragEnd(); showNotice(lockText ?? ''); }
    : dragHandlers.onDrop(slotIndex));

  useEffect(() => {
    window.addEventListener('helix:action-drag-start', dragHandlers.onGlobalDragStart);
    window.addEventListener('helix:action-drag-end', dragHandlers.onDragEnd);
    window.addEventListener('dragend', dragHandlers.onDragEnd);
    return () => {
      window.removeEventListener('helix:action-drag-start', dragHandlers.onGlobalDragStart);
      window.removeEventListener('helix:action-drag-end', dragHandlers.onDragEnd);
      window.removeEventListener('dragend', dragHandlers.onDragEnd);
    };
  }, [dragHandlers.onDragEnd, dragHandlers.onGlobalDragStart]);

  // ─── Lookup ──────────────────────────────────────────────────────────────
  const registryVersion = useSyncExternalStore(abilityRegistry.subscribe, abilityRegistry.getVersion);
  const getAbility = useCallback(
    (abilityId?: string): Ability | undefined => {
      if (!abilityId) return undefined;
      const registered = abilityRegistry.getMetadata(abilityId) ||
                         abilities.find(a => a.id === abilityId);
      return registered as Ability | undefined;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [abilities, registryVersion]
  );

  const getItem = useCallback((itemId?: string): ItemData | undefined => {
    if (!itemId) return undefined;
    const live = player.inventory?.items?.find(item => item.id === itemId);
    const canonical = ITEM_DATABASE[itemId];
    const source = live ?? canonical;
    if (!source) return undefined;
    return {
      id: source.id,
      name: source.name,
      icon: source.icon,
      description: source.description,
      rarity: source.rarity === 'unique' ? 'legendary' : source.rarity,
      quantity: live?.quantity ?? 0,
      type: source.type,
      stats: source.stats as Record<string, number> | undefined,
    };
  }, [player.inventory?.items]);

  // ─── Slot interaction ─────────────────────────────────────────────────────
  const handleClick = useCallback((slotIndex: number) => {
    const slotData = pageSlots.find(s => s.slot === slotIndex);
    gameLogger.debug('ui', `[ActionBar] handleClick(bar=${barId}, page=${currentPage}, slot=${slotIndex}):`, {
      slotData: slotData ? { slot: slotData.slot, abilityId: slotData.abilityId, type: slotData.type } : 'NOT FOUND',
    });
    if (slotData?.abilityId && onAbilityUse) {
      if (!isAbilityVisible(player, slotData.abilityId)) return;
      if (RaceGateStore.isDisabled(slotData.abilityId)) {
        try { (globalThis as any).__errorToast?.('info', '🏁 Disabled during the race'); } catch { /* optional */ }
        return;
      }
      onAbilityUse(slotData.abilityId);
      setActivatedSlot(slotIndex);
      setTimeout(() => setActivatedSlot(null), 500);
    } else if (slotData?.itemId && onItemUse) {
      onItemUse(slotData.itemId);
      setActivatedSlot(slotIndex);
      setTimeout(() => setActivatedSlot(null), 500);
    }
  }, [barId, currentPage, onAbilityUse, onItemUse, pageSlots, player]);

  const handleContextMenu = useCallback((slotIndex: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const slotData = pageSlots.find(s => s.slot === slotIndex);
    const clearable = !!slotData?.itemId
      || (!!slotData?.abilityId && isAbilityVisible(player, slotData.abilityId));
    if (clearable && locked) {
      showNotice(lockText ?? '');
      return;
    }
    if (clearable) {
      gameLogger.info('ui', `[ActionBar] Cleared bar ${barId} page ${currentPage} slot ${slotIndex}`);
      commitSlotUpdate(slotIndex, null);
    }
  }, [barId, commitSlotUpdate, currentPage, locked, lockText, pageSlots, player, showNotice]);

  const handleBarContextMenu = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement | null;
    if (target?.closest('.action-bar-slot') || target?.closest('.hudt-bar-controls')) return;
    e.preventDefault();
    setMenuOpen((prev) => !prev);
  }, []);

  // ─── Empty / filled tracking ──────────────────────────────────────────────
  const currentPageHasItems = useMemo(
    () => pageSlots.some((s) => Boolean(s.itemId || (s.abilityId && isAbilityVisible(player, s.abilityId)))),
    [pageSlots, player],
  );
  const barHasItems = useMemo(() => {
    const layoutSlots = actionBar.layout?.slots;
    if (!layoutSlots) return currentPageHasItems;
    return layoutSlots.some(
      (s) => s.bar === barId && Boolean(s.itemId || (s.abilityId && isAbilityVisible(player, s.abilityId))),
    );
  }, [actionBar.layout?.slots, barId, currentPageHasItems, player]);

  // One status row: a notice, else the drag hint (an open bar), else the lock label,
  // else the empty-bar hint on a new extra bar ("Drag abilities or items here").
  const showEmptyBarHint = barId > 1 && !currentPageHasItems && !locked;
  const tone: BarStatusTone | null = notice ? 'notice'
    : dragState.isGlobalDrag && !locked ? 'drag'
      : lockText ? 'lock'
        : showEmptyBarHint ? 'empty' : null;
  const statusText = tone === 'notice' ? notice
    : tone === 'drag' ? 'Drop ability or usable item on a slot ↓'
      : tone === 'lock' ? `🔒 ${lockText}`
        : tone === 'empty' ? 'Drag abilities or items here' : '';

  return (
    <ActionBarContainer
      className={`action-bar hudt-hotbar ${className}`.trim()}
      style={style}
      data-bar-id={barId}
      data-bar-spot={resolvedBar.spot}
      data-bar-page={currentPage}
      data-bar-pages={resolvedBar.pages}
      data-bar-slots={slotCount}
      data-locked={locked ? 'true' : 'false'}
      data-hotbar-mode={actionBar.mode ?? 'unknown'}
      onContextMenu={handleBarContextMenu}
    >
      <BarStatusLine
        $tone={tone}
        data-testid={barId === 1 ? 'action-bar-status' : `action-bar-status-${barId}`}
        role="status"
        aria-live="polite"
      >
        {statusText}
      </BarStatusLine>

      <SlotsRow style={orientation === 'vertical' ? { flexDirection: 'column' } : undefined}>
        {visibleSlots.map(slotIndex => {
          const slotData = pageSlots.find(s => s.slot === slotIndex);
          const visibleAbilityId = slotData?.abilityId && isAbilityVisible(player, slotData.abilityId)
            ? slotData.abilityId
            : undefined;
          const ability = getAbility(visibleAbilityId);
          const item = getItem(slotData?.itemId);
          const slotKey = slotKeyLabel(bindings, barId, slotIndex);
          const raceDisabled = !!slotData?.abilityId && RaceGateStore.isDisabled(slotData.abilityId);

          return (
            <ActionBarSlot
              key={slotIndex}
              barId={barId}
              page={currentPage}
              slotIndex={slotIndex}
              slotKey={slotKey}
              ability={ability}
              item={item}
              player={player}
              raceDisabled={raceDisabled}
              locked={locked}
              isDragOver={dragState.dragOverSlot === slotIndex}
              isGlobalDrag={dragState.isGlobalDrag}
              wasActivated={activatedSlot === slotIndex}
              onDragStart={dragHandlers.onDragStart(
                slotIndex,
                visibleAbilityId
                  ? { type: 'ability', abilityId: visibleAbilityId }
                  : slotData?.itemId
                    ? { type: 'item', itemId: slotData.itemId }
                    : undefined,
              )}
              onDragOver={onDragOverSlot(slotIndex)}
              onDragLeave={dragHandlers.onDragLeave(slotIndex)}
              onDrop={onDropSlot(slotIndex)}
              onDragEnd={dragHandlers.onDragEnd}
              onClick={() => handleClick(slotIndex)}
              onContextMenu={(e) => handleContextMenu(slotIndex, e)}
            />
          );
        })}

        <ActionBarControls
          barState={resolvedBar}
          allBars={allBars}
          locked={locked}
          lockText={lockText}
          currentPageHasItems={currentPageHasItems}
          barHasItems={barHasItems}
          menuOpen={menuOpen}
          onToggleMenu={() => setMenuOpen((prev) => !prev)}
          onCloseMenu={() => setMenuOpen(false)}
          onLayoutEdit={onLayoutEdit}
          orientation={orientation}
          dragLocked={dragLocked}
          onToggleDragLock={toggleHotbarDragLocked}
        />
      </SlotsRow>
    </ActionBarContainer>
  );
};
